import { getApiBaseUrl, getMailboxId, TENANT_DIRECTORY_URL } from './config';
import { getToken } from '../auth/midway';

/**
 * Lightweight API client for MailHub inbox APIs.
 *
 * Attaches the Midway JWT as a Bearer token on every request.
 * Uses the runtime API base URL (set by tenant selection).
 */

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${getApiBaseUrl()}${path}`;
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(url, {
    ...options,
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `API error ${res.status}`);
  }
  return res.json();
}

// --- Inbox API ---

export interface MessageEnvelope {
  messageId: string;
  from: string;
  to: string;
  subject: string;
  receivedAt: string;
  readStatus: string;
  labels: string[] | string | null;
  snippet?: string;
  threadId?: string;
  PK?: string;
  SK?: string;
}

/**
 * Normalize a raw API response envelope — the backend returns `timestamp`
 * but the UI uses `receivedAt`. Handles both field names gracefully.
 */
function normalizeEnvelope(raw: Record<string, unknown>): MessageEnvelope {
  return {
    ...raw,
    receivedAt: (raw.receivedAt as string) || (raw.timestamp as string) || '',
  } as MessageEnvelope;
}

export interface MessageDetail {
  message: MessageEnvelope;
  bodyHtml: string | null;
  bodyText: string | null;
  body: string | null;
}

export interface ListMessagesResponse {
  items: MessageEnvelope[];
  nextToken?: string;
}

export interface SearchResponse {
  items: MessageEnvelope[];
  query: string;
}

export async function listMessages(params?: {
  limit?: number;
  status?: 'read' | 'unread';
  nextToken?: string;
}): Promise<ListMessagesResponse> {
  const query = new URLSearchParams({ mailboxId: getMailboxId() });
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.status) query.set('status', params.status);
  if (params?.nextToken) query.set('nextToken', params.nextToken);
  const res = await request<{ items: Record<string, unknown>[]; nextToken?: string }>(`/messages?${query}`);
  return { items: res.items.map(normalizeEnvelope), nextToken: res.nextToken };
}

type RawMessageDetail = {
  message: Record<string, unknown>;
  bodyHtml: string | null;
  bodyText: string | null;
  body: string | null;
};

/**
 * Fetch a single message with its body.
 *
 * RFC 5322 Message-IDs are wrapped in angle brackets and contain `@`
 * (e.g. `<abc@mail.example.com>`). API Gateway REST API mangles those
 * characters in a path segment, so the id never reaches the Lambda intact and
 * the exact-match lookup 404s.
 *
 * Strategy: pass the id as a query parameter first (survives untouched), and
 * fall back to the path-parameter form for backends that only read the path.
 */
export async function getMessage(messageId: string): Promise<MessageDetail> {
  const mailboxId = getMailboxId();

  const byQuery = new URLSearchParams({ mailboxId, messageId });
  try {
    const res = await request<RawMessageDetail>(`/messages/detail?${byQuery}`);
    return { ...res, message: normalizeEnvelope(res.message) };
  } catch {
    // Backend doesn't support the query-parameter form — fall back to the path.
  }

  const byPath = new URLSearchParams({ mailboxId });
  const res = await request<RawMessageDetail>(
    `/messages/${encodeURIComponent(messageId)}?${byPath}`,
  );
  return { ...res, message: normalizeEnvelope(res.message) };
}

export async function searchMessages(q: string): Promise<SearchResponse> {
  const query = new URLSearchParams({ mailboxId: getMailboxId(), q });
  const res = await request<{ items: Record<string, unknown>[]; query: string }>(`/messages/search?${query}`);
  return { items: res.items.map(normalizeEnvelope), query: res.query };
}

export async function updateMessage(
  messageId: string,
  patch: { readStatus?: 'read' | 'unread'; labels?: string[] },
): Promise<{ message: MessageEnvelope }> {
  // Same as getMessage: pass the id as a query parameter so RFC 5322
  // Message-IDs (`<abc@host>`) survive API Gateway's path handling.
  const query = new URLSearchParams({ mailboxId: getMailboxId(), messageId });
  return request<{ message: MessageEnvelope }>(`/messages/detail?${query}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// --- Rule Engine API ---

export interface MailManagerRule {
  Name: string;
  Conditions: Array<{
    StringExpression?: {
      Evaluate: { Attribute: string };
      Operator: string;
      Values: string[];
    };
  }>;
  Actions: Array<{
    Drop?: Record<string, never>;
    ReplaceRecipient?: { ReplaceWith: string[] };
  }>;
}

export interface RulesResponse {
  rules: MailManagerRule[];
}

export interface RuleDef {
  condition: {
    field: 'from' | 'subject' | 'recipient' | 'to' | 'sender';
    operator: 'contains' | 'equals' | 'not-equals' | 'starts-with' | 'ends-with';
    value: string;
  };
  action: {
    type: 'forward' | 'drop';
    forwardTo?: string;
  };
}

export async function listRules(): Promise<RulesResponse> {
  return request<RulesResponse>('/rules');
}

export async function putRule(ruleId: string, def: RuleDef): Promise<{ ruleId: string; rule: MailManagerRule }> {
  return request(`/rules/${encodeURIComponent(ruleId)}`, {
    method: 'PUT',
    body: JSON.stringify(def),
  });
}

export async function deleteRule(ruleId: string): Promise<{ deleted: string }> {
  return request(`/rules/${encodeURIComponent(ruleId)}`, {
    method: 'DELETE',
  });
}


// --- Tenant Directory API ---

export interface TenantInfo {
  tenantId: string;
  displayName: string;
  mailboxId: string;
  apiEndpoint: string | null;
  domain: string | null;
  mailboxAddress: string | null;
  region: string | null;
  status: string | null;
}

export async function listTenants(): Promise<TenantInfo[]> {
  if (!TENANT_DIRECTORY_URL) return [];
  const res = await fetch(`${TENANT_DIRECTORY_URL}/tenants`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to load tenants');
  const data = await res.json();
  return data.tenants ?? [];
}
